import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl, ReactiveFormsModule } from '@angular/forms';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';
import { AuthHelper } from '../../auth/auth-helper';
import { RAAlertFormModel } from '../../form-model/report.form.model';
import { APICallStatus } from '../../models/api-call-status.mode';
import { RAAlertReportDownloadData, RAAlertReportTableData } from '../../models/report-table.model';
import { ReportService } from '../../services/report.service';
import { fromLessThanToValidator } from '../report/fromLessThanTo.validator';

import { raAlertFilter } from '../../models/ra-alert-filter.model';
import { oneMonthDateRangeValidator } from './dateRange.validator';
import * as XLSX from 'xlsx';
import { catchError, firstValueFrom, ReplaySubject, takeUntil } from 'rxjs';
import { utils } from '../../helper/utils';

@Component({
  selector: 'app-ra-alerts',
  imports: [ReactiveFormsModule],
  templateUrl: './ra-alerts.component.html',
  styleUrl: './ra-alerts.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RaAlertsComponent implements OnInit {
  today = new Date();
  openAccordion: boolean = true;
  loggedInUser = AuthHelper.user;
  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage: number = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  apiVersion: string | undefined;
  pageLimit: number = 10;
  sort: string | undefined;
  initiateRaAlertRequestApiCallStatus: APICallStatus = 'NOT_STARTED';
  columns: Array<TableColumn> = [
    {
      id: 'workOrderId',
      label: 'Work Order Number',
    },
    {
      id: 'containerNumber',
      label: 'Container Number',
    },
    {
      id: 'mode',
      label: 'Mode',
    },
    {
      id: 'totalCostUSD',
      label: 'Work Order Cost(USD)',
      dataType: {
        type: 'number',
        formatter: (value, row) => {
          return utils.numberFormatter.format(Number(value));
        },
      },
    },
    {
      id: 'shopCode',
      label: 'Shop Code',
    },
    {
      id: 'equipmentsize',
      label: 'Size',
    },
    {
      id: 'alertCategory',
      label: 'Alert Category', // Added label for alert category
    },
    {
      id: 'repairAlert',
      label: 'Repair Alert(s)',
      sortDisabled: true,
    },
  ];
  shops = AuthHelper.user?.maintenanceShops;
  data: RAAlertReportTableData[] = [];
  tableData: RAAlertReportDownloadData = {};
  raAlertFormGroup: FormGroup<RAAlertFormModel>;
  displayGridDataMsg: boolean = true;
  filterParam: raAlertFilter | undefined;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  constructor(
    private fb: FormBuilder,
    private reportService: ReportService,
  ) {
    this.raAlertFormGroup = fb.group<RAAlertFormModel>({
      fromDate: this.fb.control(''),
      toDate: this.fb.control(''),
      shopCode: this.fb.control(''),
      woNum: this.fb.control('', Validators.pattern(/\b5\d{8}\b/g)),
      containerNum: this.fb.control('', Validators.pattern(/\b[a-zA-Z]{4}\d{6,9}\b/g)),
    });
    this.raAlertFormGroup.addValidators([fromLessThanToValidator()]);
    this.raAlertFormGroup.addValidators([oneMonthDateRangeValidator()]);
  }
  ngOnInit(): void {
    this.displayGridDataMsg = true;
  }
  onApplyFilter() {
    if (this.raAlertFormGroup.valid) {
      const filters: raAlertFilter = {};
      if (this.raAlertFormGroup.controls.fromDate.value) {
        filters.fromDate = this.raAlertFormGroup.controls.fromDate.value ? this.raAlertFormGroup.controls.fromDate.value : null;
      }
      if (this.raAlertFormGroup.controls.toDate.value) {
        filters.toDate = this.raAlertFormGroup.controls.toDate.value ? this.raAlertFormGroup.controls.toDate.value : null;
      }
      if (this.raAlertFormGroup.controls.woNum.value) {
        filters.workOrderNo = this.raAlertFormGroup.controls.woNum.value ? this.raAlertFormGroup.controls.woNum.value : null;
      }
      if (this.raAlertFormGroup.controls.containerNum.value) {
        filters.containerNumber = this.raAlertFormGroup.controls.containerNum.value
          ? this.raAlertFormGroup.controls.containerNum.value
          : null;
      }

      if (this.raAlertFormGroup.controls.shopCode.value) {
        filters.shopCode = this.raAlertFormGroup.controls.shopCode.value ? this.raAlertFormGroup.controls.shopCode.value.toString() : null;
      }
      this.filterParam = filters;
      this.currentPage = 1;
      this.getRAAlerts();
    }
  }
  getRAAlerts(): void {
    this.initiateRaAlertRequestApiCallStatus = 'IN_PROGRESS';
    this.reportService.getRAAlerts(this.currentPage, this.pageLimit, this.filterParam).subscribe({
      next: (response) => {
        const headers = response.headers;
        const body = response.body || [];
        this.data = body;
        this.firstPage = headers.get('First-Page') ? +headers.get('First-Page')! : undefined;
        this.previousPage = headers.get('Previous-Page') ? +headers.get('Previous-Page')! : undefined;
        this.currentPage = +headers.get('Current-Page')!;
        this.nextPage = headers.get('Next-Page') ? +headers.get('Next-Page')! : undefined;
        this.lastPage = headers.get('Last-Page') ? +headers.get('Last-Page')! : undefined;
        this.totalItemCount = headers.get('Total-Item-Count') ? +headers.get('Total-Item-Count')! : undefined;
        this.apiVersion = headers.get('API-Version') ?? undefined;
        this.initiateRaAlertRequestApiCallStatus = 'COMPLETED_SUCCESFULLY';
        this.displayGridDataMsg = false;
      },
      error: (err) => {
        this.data = [];
        this.displayGridDataMsg = false;
        this.initiateRaAlertRequestApiCallStatus = 'FAILED';
      },
    });
  }
  async onDownload() {
    let formValues = this.raAlertFormGroup.getRawValue();
    const formatedFromDate = this.formatDate(formValues.fromDate);
    const formatedToDate = this.formatDate(formValues.toDate);
    let fileName = 'repair-alerts' + '_' + formatedFromDate + '_' + formatedToDate + '_' + Date.now();

    this.tableData = await firstValueFrom(
      this.reportService.onDownloadAlerts(this.filterParam).pipe(
        takeUntil(this.destroyed$),
        catchError((err) => {
          throw err;
        }),
      ),
    );
    const pagedData = this.tableData?.pagedData || [];
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(pagedData);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, fileName + '.xlsx');
  }
  isControlInErrorState(control: FormControl): boolean | null {
    if (control.invalid && control.touched) {
      return true;
    }
    return null;
  }
  toggleAccordionPanel() {
    this.openAccordion = !this.openAccordion;
  }
  formatDate(input: string | null) {
    if (input) {
      const [day, month, year] = input.split('-').map(Number);
      const inputDate = new Date(year, month - 1, day);
      return inputDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }).replace(/[^a-zA-Z0-9]/g, '');
    }
    return;
  }
  onPageLimitChange(event: Event) {
    let customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;
    this.getRAAlerts();
  }

  onPageChange(event: Event) {
    let customEvent = event as CustomEvent;
    let changedPage = customEvent.detail as number;
    if (changedPage != this.currentPage) {
      this.currentPage = changedPage;
      this.getRAAlerts();
    }
  }

  get isAnyFilterFilled(): boolean {
    const value = this.raAlertFormGroup.value;
    return Object.values(value).some((v) => v && v.toString().trim() !== '');
  }
}
